/** Trade facts + the plain-language "Why did this trade happen?" for one TEK_RSI_MOMENTUM trade. */
import { Alert, Box, Card, CardContent, Chip, Divider, List, ListItem, ListItemIcon, ListItemText, Stack, Table, TableBody, TableCell, TableRow, Typography } from "@mui/material";
import type { RsiTradeReview } from "@/types";
import { inr, istLabel, num } from "./tradeReviewChartData";

export default function TradeDetailsPanel({ review }: { review: RsiTradeReview }) {
  const s = review.summary;
  const ex = review.explanation;
  const o = s.option;
  const rows: [string, string][] = [
    ["Trade date", s.trade_date],
    ["Direction", `${s.side} (${o.option_type === "CE" ? "bought call" : "bought put"})`],
    ["Mode", s.mode === "LIVE" ? "LIVE (real broker orders)" : "PAPER (simulated fills on real quotes)"],
    ["Entry", `${istLabel(s.entry_time_ist)} · NIFTY ${num(s.entry_price)}`],
    ["Stop-loss", `${num(s.initial_stop)}${s.final_stop !== null && s.final_stop !== s.initial_stop ? ` → ${num(s.final_stop)} (${s.stop_kind.toLowerCase()})` : ""}`],
    ["Target", `${num(s.target)}${s.first_target !== null ? ` (first target ${num(s.first_target)})` : ""}`],
    ["Exit", s.exit_time_ist ? `${istLabel(s.exit_time_ist)} · NIFTY ${num(s.exit_price)}` : "Open"],
    ["Exit reason", s.exit_reason ?? "—"],
    ["Realized P&L", inr(s.realized_pnl)],
    ["R-multiple", s.r_multiple === null ? "—" : `${s.r_multiple >= 0 ? "+" : ""}${s.r_multiple.toFixed(2)}R (NIFTY points)${s.r_multiple_money !== null ? ` · ${s.r_multiple_money.toFixed(2)}R (₹)` : ""}`],
    ["Max favourable move", `${num(s.mfe_points)} pts`],
    ["Max adverse move", `${num(s.mae_points)} pts`],
    ["Holding time", s.holding_minutes === null ? "—" : `${s.holding_minutes.toFixed(1)} min`],
    ["NIFTY move (in trade direction)", s.nifty_move_points === null ? "—" : `${s.nifty_move_points >= 0 ? "+" : ""}${num(s.nifty_move_points)} pts`],
    ["Option contract", `${o.contract} × ${o.quantity}${o.lots ? ` (${o.lots} lots)` : ""}`],
    ["Option premium", `${inr(o.entry_premium)} → ${inr(o.exit_premium)}`],
    ["Option P&L", inr(o.option_pnl)],
  ];
  return (
    <Card variant="outlined" data-testid="trade-details">
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle1" fontWeight={700}>Why did this trade happen?</Typography>
          <Chip size="small" label={s.side} color={s.side === "LONG" ? "success" : "error"} />
          {s.outcome && <Chip size="small" variant="outlined" label={s.outcome === "PROFIT" ? "Profit" : "Loss"} color={s.outcome === "PROFIT" ? "success" : "error"} />}
          <Chip size="small" variant="outlined" label="Actual algo trade data" />
        </Stack>
        <Box data-testid="why-panel">
          <Typography variant="body2" fontWeight={600}>{ex.headline}</Typography>
          <List dense disablePadding>
            {ex.entry_reasons.map((r) => (
              <ListItem key={r.name} disableGutters sx={{ py: 0 }}>
                <ListItemIcon sx={{ minWidth: 24, color: r.passed ? "success.main" : "error.main" }}>{r.passed ? "✓" : "✗"}</ListItemIcon>
                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={r.text} />
              </ListItem>
            ))}
          </List>
          {ex.reasons_source === "RECOMPUTED" && (
            <Typography variant="caption" color="warning.main">Signal log not found — reasons recomputed from stored candles.</Typography>
          )}
          <Typography variant="body2" fontWeight={600} sx={{ mt: 1 }}>Exit reason:</Typography>
          <List dense disablePadding>
            {ex.exit_lines.map((l) => (
              <ListItem key={l} disableGutters sx={{ py: 0 }}>
                <ListItemText primaryTypographyProps={{ variant: "body2" }} primary={`• ${l}`} />
              </ListItem>
            ))}
          </List>
          {ex.blocked.length > 0 && (
            <Alert severity="info" sx={{ mt: 1 }} data-testid="blocked-signals">
              Other signals this session that were blocked: {ex.blocked.join(" · ")}
            </Alert>
          )}
        </Box>
        <Divider sx={{ my: 1.5 }} />
        <Table size="small">
          <TableBody>
            {rows.map(([k, v]) => (
              <TableRow key={k}>
                <TableCell sx={{ color: "text.secondary", width: "42%" }}>{k}</TableCell>
                <TableCell>{v}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
